import { useEffect, useState } from 'react';
import { en } from './en';
import { ru } from './ru';

const DICT = { en, ru } as const;
export type Lang = keyof typeof DICT;
export type TKey = keyof typeof en;

const LS_KEY = 'cradle7.lang';

function detectDefaultLang(): Lang {
  // RU-only ship: always start in Russian regardless of browser locale or
  // any previously-stored preference. The Settings toggle still flips state
  // in-memory for preview, but a refresh restores RU.
  return 'ru';
}

let current: Lang = detectDefaultLang();
const listeners = new Set<() => void>();

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  if (!(lang in DICT)) return;
  if (lang === current) return;
  current = lang;
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LS_KEY, lang);
    }
  } catch {
    /* ignore */
  }
  listeners.forEach((fn) => fn());
}

/**
 * Translate a key into the active language.
 * Falls back to English if the key is missing in the active dict,
 * then to the raw key if missing in English as well.
 */
export function t(key: TKey): string {
  const active = DICT[current][key];
  if (active !== undefined) return active;
  const fallback = DICT.en[key];
  if (fallback !== undefined) return fallback;
  return key;
}

/** React hook: re-renders the component whenever the active language changes. */
export function useT(): { t: typeof t; lang: Lang; setLang: typeof setLang } {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return { t, lang: current, setLang };
}
